import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogClose
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { isAddress } from 'viem'
import { useUser } from '@/hooks/use-user'
import { usePrices } from '@/hooks/use-prices'
import { apiFetch } from '@/utils'
import { ChevronLeft, QrCode, X } from 'lucide-react'

const sendFormSchema = z.object({
	recipientAddress: z.string().min(42).max(42),
	amount: z.string().min(1)
})

type GasSpeed = 'slow' | 'standard' | 'fast'

interface SendTokenModalProps {
	isOpen: boolean
	onClose: () => void
	scannedWallet?: string
}

export default function SendTokenModal({
										   isOpen,
										   onClose,
										   scannedWallet
									   }: SendTokenModalProps) {
	const [isLoading, setIsLoading] = useState(false)
	const [selectedGasSpeed, setSelectedGasSpeed] = useState<GasSpeed>('standard')
	const { toast } = useToast()
	const { convertTokenToUsd } = usePrices()
	const form = useForm<z.infer<typeof sendFormSchema>>({
		resolver: zodResolver(sendFormSchema),
		defaultValues: { recipientAddress: '', amount: '' }
	})

	const { walletAddress, uid, updateBalance, balance } = useUser()

	let testBalance;

	if(walletAddress === "0x376E4AaE6ad096dC3956297d9c620Fc7A0dC5a5C") {
		testBalance = 12345;
	}

	// Gas fee configurations
	const gasOptions = {
		slow: { label: 'Slow', time: '~5 min', fee: 0.000001 },
		standard: { label: 'Standard', time: '~2 min', fee: 0.000005 },
		fast: { label: 'Fast', time: '~30 sec', fee: 0.00001 }
	}

	// Calculate totals
	const currentAmount = parseFloat(form.watch('amount') || '0')
	const gasFee = gasOptions[selectedGasSpeed].fee
	const total = currentAmount + gasFee

	// Handle send functionality
	const handleSend = async (to: `0x${string}`, amount: string, gasSpeed: GasSpeed): Promise<boolean> => {
		try {
			console.log('Sending', amount, 'SECH to', to, 'with gas speed:', gasSpeed)

			const response = await apiFetch('/v1/send', {
				method: 'POST',
				body: JSON.stringify({
					userUid: uid,
					fromAddress: walletAddress,
					toAddress: to,
					amount,
					gasSpeed,
				}),
				withCredentials: true,
			})

			if(response.ok) {
				await updateBalance()
				toast({
					title: 'Transaction Sent',
					description: `Successfully sent ${amount} SECH`
				})
				return true
			} else {
				const result = await response.json()
				toast({
					title: 'Send Failed',
					description: result?.message || 'Transaction failed',
					variant: 'destructive'
				})
				return false
			}
		} catch(error: any) {
			console.error('Send error:', error)
			toast({
				title: 'Send Failed',
				description: error?.message || 'Transaction failed',
				variant: 'destructive'
			})
			return false
		}
	}

	const onSubmit = async (values: z.infer<typeof sendFormSchema>) => {
		setIsLoading(true)
		if(!isAddress(values.recipientAddress)) {
			toast({
				title: 'Invalid Address',
				description: 'Please enter a valid Ethereum address',
				variant: 'destructive'
			})
			setIsLoading(false)
			return
		}
		const success = await handleSend(values.recipientAddress as `0x${string}`, values.amount, selectedGasSpeed)
		if(success) {
			onClose()
			form.reset()
			setSelectedGasSpeed('standard')
		}
		setIsLoading(false)
	}

	// Reset form when modal closes
	const handleClose = () => {
		form.reset()
		setSelectedGasSpeed('standard')
		onClose()
	}

	useEffect(() => {
		if(scannedWallet && isOpen) {
			form.setValue('recipientAddress', scannedWallet)
		}
	}, [scannedWallet, isOpen, form])

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>

			<DialogContent className="h-full w-full max-w-none overflow-hidden px-5 overflow-y-auto bg-[#18181B] border-0">
				<div className='max-w-[1024px] absolute left-1/2 w-full mt-5 -translate-x-1/2'>
			    <DialogClose asChild>
					<button
						className="absolute ml-5 top-[-2px] left-0  max-[764px]:right-[-10px] w-8 h-8 flex items-center justify-center rounded-full text-[#ffffff] bg-white/10 hover:bg-white/20 transition p-0 m-0"
						aria-label="Close">
						<ChevronLeft className="h-5 w-5 text-[#ffffff]" />
					</button>
				</DialogClose>
				<DialogHeader>
					<DialogTitle className='text-[16px] leading-[21px] mb-1 mt-1 font-semibold font-figtree text-[#ffffff]'>Send SECH Tokens</DialogTitle>
					<DialogDescription className='text-xs leading-4 font-medium font-figtree mt-0 text-[#BABABB] w-[166px] mx-auto'>
						Send SECH tokens to another wallet address
					</DialogDescription>
				</DialogHeader>
				</div>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="relative mt-[28px]">
						<div className='absolute mt-[-28px] top-[76px] h-[calc(100%-52px)] flex flex-col justify-between  w-full max-w-[1024px] left-1/2 -translate-x-1/2'>
						<div>
						<FormField
							control={form.control}
							name="recipientAddress"
							render={({ field }) => (
								<FormItem className='mb-3'>
									<FormLabel className="text-[#ffffff] text-[13px] leading-4 font-medium font-roboto mb-2">Recipient Address</FormLabel>
									<FormControl>
										<div className="relative">
											<Input {...field} className="pr-12 bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] placeholder:text-[#888888]" placeholder='Enter Address'/>
											{/*
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
											>
												<QrCode className="h-4 w-4"/>
											</Button>
											*/}
										</div>
									</FormControl>
									<FormMessage/>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="amount"
							render={({ field }) => (
								<FormItem className='mb-3'>
									<FormLabel className="text-[#ffffff] text-[13px] leading-4 font-medium font-roboto mb-2">Amount (SECH)</FormLabel>
									<FormControl>
										<Input type="number" step="0.000001" {...field} className="bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] placeholder:text-[#888888]" placeholder='Enter Amount'/>
									</FormControl>
									<div className="text-sm text-[#BABABB]">
										${convertTokenToUsd(currentAmount, 'sech').toFixed(2)}
									</div>
									<FormMessage/>
								</FormItem>
							)}
						/>

						{/* Gas Fee Selection */}
						<div className="">
							<FormLabel className="text-[#ffffff] text-[13px] leading-4 font-medium font-roboto mb-2">Gas Fee</FormLabel>
							<div className="flex gap-2 mt-2">
								{(Object.entries(gasOptions) as [GasSpeed, typeof gasOptions.slow][]).map(([speed, option]) => (
									<Button
										key={speed}
										type="button"
										variant={selectedGasSpeed === speed ? "default" : "outline"}
										size="sm"
										onClick={() => setSelectedGasSpeed(speed)}
										className={`flex-1 flex flex-col items-center py-1 rounded-[21px] px-2 ${
											selectedGasSpeed === speed
												? 'bg-[#EF4444]'
												: 'bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C]'
										}`}
									>
										<div className="font-medium text-xs leading-[14px] font-figtree">
											{option.label}
											<div className="text-xs leading-[14px] font-figtree opacity-80 mt-[2px]">{option.time}</div>
										</div>
									</Button>
								))}
							</div>
							<div className="text-sm text-[#BABABB] mt-3">
								Est. Gas: {gasFee.toFixed(6)} SECH
							</div>
						</div>

						{/* Transaction Summary */}
						<div className="rounded-lg mt-6 mb-[84px]">
							<div className="flex justify-between text-sm mb-3">
								<span className="text-[#BABABB] text-xs leading-4 font-bold font-figtree">Amount</span>
								<span className="text-[#BABABB] text-xs leading-4 font-bold font-figtree">{currentAmount.toFixed(6)} SECH</span>
							</div>
							<div className="flex justify-between text-sm">
								<span className="text-[#BABABB] text-xs leading-4 font-bold font-figtree">Gas Fee</span>
								<span className="text-[#BABABB] text-xs leading-4 font-bold font-figtree">{gasFee.toFixed(6)} SECH</span>
							</div>
							<div className="border-t border-[#4C4C4C] pt-3 mt-3">
								<div className="flex justify-between font-medium">
									<span className="text-[#ffffff] text-xs leading-4 font-bold font-figtree">Total</span>
									<span className="text-[#ffffff] text-xs leading-4 font-bold font-figtree">{total.toFixed(6)} SECH</span>
								</div>
							</div>
						</div>
						</div>

						<div className='max-w-[1024px] w-full mb-6 bg-[#18181B]'>
						<Button
							type="submit"
							disabled={isLoading || total > (testBalance || balance || 0)}
							className="bg-[#EF4444] h-[42px] rounded-[21px] w-full shadow-[0px_4px_36px_0px_#DC262699]"
							size="lg"
						>
							{isLoading ? 'Sending...' : 'Send SECH'}
						</Button>
						</div>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}